// Знакомство с кабинетом после онбординга (решение владельца 2026-10-19; заменило две плашки-подсказки). С 2026-10-23 —
// три шага по разделам навигации (навигация, понятная с первого входа в кабинет): «Главная» — что
// сделать сегодня, «Слова и модули» — модули и новые карточки, «Грамматика» (с 2026-10-23 — основной раздел) — правила
// с упражнениями, «Игра» — разговоры в Лондоне за жетоны. Элементы —
// пункты боковой панели на компьютере и вкладки внизу на телефоне (`data-tour`).
// Ключ браузерный, поэтому знакомство включает подсветку каждый раз: у аккаунта знакомство одно,
// а второй аккаунт в том же браузере иначе остался бы без неё. «Понятно» и «Пропустить» выключают.
//
// Значение ключа: нет — не показывать (давние ученики), '0' — показать, '1' — пройдена или пропущена.
// Модуль без импортов приложения: его проверяет tests/onboarding.test.ts под Node.

export const DASHBOARD_TOUR_KEY = 'lx.tour.dashboard';

export type TourStepId = 'home' | 'words' | 'grammar' | 'game';

export interface TourStep {
  id: TourStepId;
}

/** Элемент шага ищется по `data-tour="<id>"`. */
export const DASHBOARD_TOUR_STEPS: readonly TourStep[] = [{ id: 'home' }, { id: 'words' }, { id: 'grammar' }, { id: 'game' }];

/**
 * Шаги, чей элемент есть при старте подсветки: их число — M в «N из M». Пункты навигации есть всегда, но раскладка
 * может спрятать какой-то из них. Элемент пропал уже в ходе показа — шаг пропускается.
 */
export function presentTourSteps(steps: readonly TourStep[], has: (id: TourStepId) => boolean): TourStep[] {
  return steps.filter((step) => has(step.id));
}

export type TourStore = Pick<Storage, 'getItem' | 'setItem'>;

function localStore(): TourStore | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null; // хранилище заблокировано настройками приватности — подсветки просто не будет
  }
}

/** Знакомство пройдено: показать подсветку на главной. */
export function armDashboardTour(store: TourStore | null = localStore()): void {
  try {
    store?.setItem(DASHBOARD_TOUR_KEY, '0');
  } catch {
    // нет места или запрет записи — подсветки не будет, это не ошибка
  }
}

export function isDashboardTourArmed(store: TourStore | null = localStore()): boolean {
  try {
    return store?.getItem(DASHBOARD_TOUR_KEY) === '0';
  } catch {
    return false;
  }
}

/** «Понятно» на последнем шаге или «Пропустить». */
export function finishDashboardTour(store: TourStore | null = localStore()): void {
  try {
    store?.setItem(DASHBOARD_TOUR_KEY, '1');
  } catch {
    // запись не удалась — подсветка покажется ещё раз, это не страшно
  }
}

export interface TourRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface TourSize {
  width: number;
  height: number;
}

export const TOUR_GAP = 14;
export const TOUR_MARGIN = 16;

/**
 * Где поставить окошко с текстом: `right` — справа от пункта меню (боковая панель на компьютере),
 * иначе под элементом, а если снизу не помещается — над ним (вкладки внизу телефона). Окошко всегда целиком на экране.
 */
export function placeTourTip(
  target: TourRect,
  tip: TourSize,
  viewport: TourSize,
  prefer: 'right' | 'below' = 'below',
): { top: number; left: number } {
  const maxLeft = Math.max(TOUR_MARGIN, viewport.width - tip.width - TOUR_MARGIN);
  const maxTop = Math.max(TOUR_MARGIN, viewport.height - tip.height - TOUR_MARGIN);
  const clamp = (value: number, max: number) => Math.min(Math.max(value, TOUR_MARGIN), max);

  const rightLeft = target.left + target.width + TOUR_GAP;
  if (prefer === 'right' && rightLeft + tip.width + TOUR_MARGIN <= viewport.width) {
    return { top: clamp(target.top + target.height / 2 - tip.height / 2, maxTop), left: rightLeft };
  }

  const below = target.top + target.height + TOUR_GAP;
  const above = target.top - TOUR_GAP - tip.height;
  const fitsBelow = below + tip.height + TOUR_MARGIN <= viewport.height;
  const top = fitsBelow || above < TOUR_MARGIN ? below : above;
  // По горизонтали — к краю элемента, ближнему к середине экрана: у вкладки справа окошко встаёт левее неё,
  // у пункта слева — правее.
  const alignRight = target.left + target.width / 2 > viewport.width / 2;
  const left = alignRight ? target.left + target.width - tip.width : target.left;
  return { top: clamp(top, maxTop), left: clamp(left, maxLeft) };
}
